import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Button, Card, Col, DatePicker, Divider, Empty, Row, Segmented, Space, Spin, Statistic, Tag, Typography,
} from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, ReloadOutlined } from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import dayjs, { type Dayjs } from 'dayjs'
import type { EtfFlowItem, EtfFlowsResponse, FearGreedResponse } from '../../types/api'
import {
  FNG_RANGE_PRESETS,
  type FngRangePreset,
  fngColor,
  fngEmoji,
} from '../../constants/fearGreed'
import {
  ETF_TYPE_COLORS,
  FNG_REFRESH_MS,
  etfFlowColor,
  formatUsdCompact,
} from '../../constants/marketSentiment'

const { Text, Title } = Typography
const { RangePicker } = DatePicker

const ETF_TYPES = ['us-btc-spot', 'us-eth-spot', 'us-sol-spot'] as const

type MarketSentimentTabProps = {
  enabled: boolean
  isMobile: boolean
  fetchFearGreed: (limit?: number) => Promise<FearGreedResponse>
  fetchEtfFlows: (lookbackDays?: number) => Promise<EtfFlowsResponse>
}

function resolveLookbackDays(preset: FngRangePreset, customRange: [Dayjs, Dayjs] | null): number {
  if (preset === 'custom' && customRange) {
    return Math.min(300, Math.max(1, customRange[1].diff(customRange[0], 'day') + 1))
  }
  if (preset === 'custom') return 30
  return preset
}

function filterByDateRange<T extends { date: string }>(
  rows: T[],
  preset: FngRangePreset,
  customRange: [Dayjs, Dayjs] | null,
): T[] {
  if (preset !== 'custom' || !customRange) return rows
  const [start, end] = customRange
  return rows.filter((p) => {
    const d = dayjs(p.date)
    return (
      (d.isAfter(start, 'day') || d.isSame(start, 'day'))
      && (d.isBefore(end, 'day') || d.isSame(end, 'day'))
    )
  })
}

function filterFngSeries(
  series: FearGreedResponse['series'],
  preset: FngRangePreset,
  customRange: [Dayjs, Dayjs] | null,
): FearGreedResponse['series'] {
  if (preset === 'custom') return filterByDateRange(series, preset, customRange)
  if (preset === 360) return series.slice(-360)
  return series.slice(-preset)
}

function latestEtfByType(items: EtfFlowItem[]): EtfFlowItem[] {
  const map = new Map<string, EtfFlowItem>()
  for (const item of items) {
    const prev = map.get(item.etf_type)
    if (!prev || item.date > prev.date) map.set(item.etf_type, item)
  }
  return ETF_TYPES.map((t) => map.get(t)).filter(Boolean) as EtfFlowItem[]
}

function buildFngChartOption(series: FearGreedResponse['series'], isMobile: boolean): EChartsOption {
  const dates = series.map((p) => p.date)
  const values = series.map((p) => p.value)
  return {
    grid: { left: isMobile ? 40 : 48, right: isMobile ? 12 : 24, top: 36, bottom: isMobile ? 56 : 48 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: unknown) => {
        const row = Array.isArray(params) ? params[0] : null
        if (!row || row.dataIndex == null) return ''
        const p = series[row.dataIndex]
        if (!p) return ''
        return `${p.date}<br/>指数 ${p.value} · ${p.classification_zh}`
      },
    },
    visualMap: {
      show: false,
      dimension: 1,
      pieces: [
        { lte: 24, color: '#cf1322' },
        { gt: 24, lte: 44, color: '#fa541c' },
        { gt: 44, lte: 55, color: '#d4b106' },
        { gt: 55, lte: 74, color: '#52c41a' },
        { gt: 74, color: '#237804' },
      ],
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: isMobile ? 45 : 0,
        fontSize: isMobile ? 10 : 12,
        formatter: (v: string) => (isMobile ? v.slice(5) : v),
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      splitLine: { lineStyle: { type: 'dashed', opacity: 0.35 } },
    },
    series: [{
      name: '恐慌贪婪指数',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: isMobile ? 4 : 6,
      data: values,
      areaStyle: { opacity: 0.12 },
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#999' },
        data: [{ yAxis: 50, label: { formatter: '中性 50', fontSize: 10 } }],
      },
    }],
  }
}

function buildEtfChartOption(items: EtfFlowItem[], isMobile: boolean): EChartsOption {
  const byDate = new Map<string, Record<string, number>>()
  for (const item of items) {
    if (!byDate.has(item.date)) byDate.set(item.date, {})
    byDate.get(item.date)![item.etf_type] = item.net_inflow_usd / 1e6
  }
  const dates = Array.from(byDate.keys()).sort()
  const series = ETF_TYPES.filter((t) => items.some((i) => i.etf_type === t)).map((t) => ({
    name: items.find((i) => i.etf_type === t)?.etf_type_label || t,
    type: 'bar' as const,
    emphasis: { focus: 'series' as const },
    itemStyle: { color: ETF_TYPE_COLORS[t] },
    data: dates.map((d) => byDate.get(d)?.[t] ?? 0),
  }))

  return {
    grid: { left: isMobile ? 44 : 56, right: isMobile ? 8 : 16, top: 40, bottom: isMobile ? 56 : 48 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        const date = params[0]?.axisValue
        const lines = [`${date}`]
        for (const p of params) {
          const val = Number(p.value)
          lines.push(`${p.seriesName}: ${formatUsdCompact(val * 1e6)}`)
        }
        return lines.join('<br/>')
      },
    },
    legend: { top: 0, type: isMobile ? 'scroll' : 'plain' },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: isMobile ? 45 : 0,
        fontSize: isMobile ? 10 : 12,
        formatter: (v: string) => (isMobile ? v.slice(5) : v),
      },
    },
    yAxis: {
      type: 'value',
      name: '百万美元',
      nameTextStyle: { fontSize: 11 },
      splitLine: { lineStyle: { type: 'dashed', opacity: 0.35 } },
    },
    series,
  }
}

export default function MarketSentimentTab({
  enabled,
  isMobile,
  fetchFearGreed,
  fetchEtfFlows,
}: MarketSentimentTabProps) {
  const [preset, setPreset] = useState<FngRangePreset>(30)
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null)

  const fetchLimit = preset === 'custom' ? 0 : Math.max(preset, 30)
  const etfLookback = resolveLookbackDays(preset, customRange)

  const fngQuery = useQuery<FearGreedResponse>({
    queryKey: ['fear-greed', fetchLimit],
    queryFn: () => fetchFearGreed(fetchLimit),
    enabled,
    staleTime: FNG_REFRESH_MS,
    refetchInterval: enabled ? FNG_REFRESH_MS : false,
    refetchIntervalInBackground: false,
  })

  const etfQuery = useQuery<EtfFlowsResponse>({
    queryKey: ['etf-flows', etfLookback],
    queryFn: () => fetchEtfFlows(etfLookback),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  const chartSeries = useMemo(() => {
    if (!fngQuery.data?.series?.length) return []
    return filterFngSeries(fngQuery.data.series, preset, customRange)
  }, [fngQuery.data?.series, preset, customRange])

  const etfItems = useMemo(() => {
    if (!etfQuery.data?.items?.length) return []
    return filterByDateRange(etfQuery.data.items, preset, customRange)
  }, [etfQuery.data?.items, preset, customRange])

  const fngChartOption = useMemo(
    () => (chartSeries.length ? buildFngChartOption(chartSeries, isMobile) : null),
    [chartSeries, isMobile],
  )

  const etfChartOption = useMemo(
    () => (etfItems.length ? buildEtfChartOption(etfItems, isMobile) : null),
    [etfItems, isMobile],
  )

  const latestEtfs = useMemo(() => latestEtfByType(etfItems), [etfItems])
  const current = fngQuery.data?.current
  const isFetching = fngQuery.isFetching || etfQuery.isFetching

  const refreshAll = () => {
    void fngQuery.refetch()
    void etfQuery.refetch()
  }

  return (
    <div className="market-sentiment-tab">
      <Space style={{ marginBottom: 12 }} wrap>
        <Segmented
          value={preset}
          onChange={(v) => setPreset(v as FngRangePreset)}
          options={FNG_RANGE_PRESETS.map((p) => ({ label: p.label, value: p.key }))}
        />
        {preset === 'custom' ? (
          <RangePicker
            value={customRange}
            onChange={(vals) => setCustomRange(vals as [Dayjs, Dayjs] | null)}
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            allowClear
            inputReadOnly={isMobile}
          />
        ) : null}
        <Button icon={<ReloadOutlined />} loading={isFetching} onClick={refreshAll}>
          刷新
        </Button>
      </Space>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        恐慌指数约每 1 小时自动刷新 · ETF 资金流在美股收盘后（美东约 17:00）每日更新一次，打开本页或点刷新可获取
      </Text>

      {fngQuery.error ? (
        <Alert type="error" showIcon message="恐慌指数加载失败" style={{ marginBottom: 12 }} />
      ) : null}
      {etfQuery.error ? (
        <Alert type="error" showIcon message="ETF 资金流加载失败" style={{ marginBottom: 12 }} />
      ) : null}

      {fngQuery.isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : !fngQuery.data ? (
        <Empty description="恐慌指数暂无数据" />
      ) : (
        <>
          {current ? (
            <Card size="small" className="fear-greed-current-card" style={{ marginBottom: 16 }}>
              <div className={`fear-greed-current ${isMobile ? 'mobile' : ''}`}>
                <div
                  className="fear-greed-icon"
                  style={{ background: `${fngColor(current.value)}18`, borderColor: fngColor(current.value) }}
                  aria-hidden
                >
                  <span style={{ fontSize: isMobile ? 36 : 44 }}>{fngEmoji(current.classification_zh)}</span>
                </div>
                <div className="fear-greed-current-text">
                  <Text type="secondary">恐慌贪婪指数 · {current.date}</Text>
                  <Space align="baseline" size={12} wrap>
                    <Statistic
                      value={current.value}
                      suffix="/ 100"
                      valueStyle={{ color: fngColor(current.value), fontSize: isMobile ? 32 : 40, fontWeight: 700 }}
                    />
                    <Title level={4} style={{ margin: 0, color: fngColor(current.value) }}>
                      {current.classification_zh}
                    </Title>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    数据来源 {fngQuery.data.source}
                    {fngQuery.data.stale ? ' · 缓存' : ''}
                  </Text>
                </div>
              </div>
            </Card>
          ) : null}

          {fngChartOption ? (
            <Card size="small" title="恐慌指数走势" bodyStyle={{ padding: isMobile ? '8px 4px' : '12px 16px' }} style={{ marginBottom: 16 }}>
              <ReactECharts option={fngChartOption} style={{ height: isMobile ? 260 : 320, width: '100%' }} notMerge lazyUpdate />
            </Card>
          ) : (
            <Empty description={preset === 'custom' && !customRange ? '请选择自定义日期范围' : '所选范围暂无恐慌指数'} style={{ marginBottom: 16 }} />
          )}
        </>
      )}

      <Divider orientation="left" plain>美国现货 ETF 资金流</Divider>

      {etfQuery.isLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : !etfQuery.data ? (
        <Empty description="ETF 资金流暂无数据" />
      ) : (
        <>
          {latestEtfs.length > 0 ? (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              {latestEtfs.map((row) => (
                <Col xs={24} sm={8} key={row.etf_type}>
                  <Card size="small" className="etf-flow-latest-card">
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space wrap>
                        <Tag color={ETF_TYPE_COLORS[row.etf_type]}>{row.etf_type_label}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>{row.date}</Text>
                      </Space>
                      <Statistic
                        title="日净流入"
                        value={formatUsdCompact(row.net_inflow_usd)}
                        valueStyle={{ color: etfFlowColor(row.net_inflow_usd), fontSize: isMobile ? 20 : 24 }}
                        prefix={row.is_inflow ? <ArrowUpOutlined /> : row.is_outflow ? <ArrowDownOutlined /> : undefined}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>{row.flow_direction_zh}</Text>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : null}

          {etfChartOption ? (
            <Card
              size="small"
              title="ETF 日净流入走势（百万美元）"
              extra={etfQuery.data.stale ? <Text type="warning">缓存</Text> : null}
              bodyStyle={{ padding: isMobile ? '8px 4px' : '12px 16px' }}
            >
              <ReactECharts option={etfChartOption} style={{ height: isMobile ? 280 : 360, width: '100%' }} notMerge lazyUpdate />
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                正=净流入（申购）· 负=净流出（赎回）· 数据源美股收盘后日更
                {etfQuery.data.fetched_at ? ` · 拉取于 ${dayjs(etfQuery.data.fetched_at).format('MM-DD HH:mm')}` : ''}
              </Text>
            </Card>
          ) : (
            <Empty description={preset === 'custom' && !customRange ? '请选择自定义日期范围' : '所选范围暂无 ETF 数据'} />
          )}
        </>
      )}
    </div>
  )
}
